import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, LockOpen } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

/**
 * Owner-only PIN lock wrapper for sensitive sections (Reports / Operators /
 * Recycle Bin / Settings). When the section is first opened in a session the
 * user is prompted for a 4-digit PIN. Once entered, the section stays unlocked
 * for the rest of the browser session.
 *
 * The PIN itself lives on the backend (bcrypt-hashed in the settings
 * collection). Frontend never knows the actual PIN — it just POSTs the
 * entered value to `/api/settings/owner-pin/verify` and unlocks on success.
 * Default value is "1380" until the owner rotates it from Settings > Security.
 */
const STORAGE_KEY = "sc_owner_pin_unlocked_v1";

function readUnlocked() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}
function writeUnlocked(map) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map)); }
  catch (_e) { /* sessionStorage unavailable — proceed unpersisted */ }
}

export function isSectionUnlocked(key) {
  return !!readUnlocked()[key];
}

export function lockAllSections() {
  try { sessionStorage.removeItem(STORAGE_KEY); }
  catch (_e) { /* ignore */ }
}

export default function PinLock({ sectionKey, sectionLabel, children, onCancel }) {
  const nav = useNavigate();
  const [unlocked, setUnlocked] = useState(() => isSectionUnlocked(sectionKey));
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // When user hits Esc / clicks the built-in X, we bail out cleanly.
  // If the parent provided `onCancel` (e.g. AdminDashboard resetting its
  // section state), use that; otherwise fall back to navigating home.
  const cancelLock = () => {
    setPin(""); setErr("");
    if (typeof onCancel === "function") { onCancel(); return; }
    nav("/", { replace: true });
  };

  useEffect(() => {
    setUnlocked(isSectionUnlocked(sectionKey));
    setPin(""); setErr("");
  }, [sectionKey]);

  useEffect(() => {
    if (!unlocked && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [unlocked]);

  const tryUnlock = async (val) => {
    if (busy) return;
    if (!/^\d{4}$/.test(val)) {
      setErr("PIN 4 digits ka hona chahiye.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/settings/owner-pin/verify", { pin: val });
      const m = readUnlocked();
      m[sectionKey] = true;
      writeUnlocked(m);
      setUnlocked(true);
      setPin("");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Galat PIN. Please dobara try karo.");
      setPin("");
      setTimeout(() => inputRef.current?.focus(), 30);
    } finally {
      setBusy(false);
    }
  };

  if (unlocked) return children;

  return (
    <Dialog open modal onOpenChange={(v) => { if (!v) cancelLock(); }}>
      <DialogContent
        className="max-w-sm bg-slate-900 border-slate-700 text-white rounded-sm"
        data-testid={`pin-lock-dialog-${sectionKey}`}
        onEscapeKeyDown={(e) => { e.preventDefault(); cancelLock(); }}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400 flex items-center gap-1">
            <Lock size={12} weight="fill" /> // OWNER ACCESS
          </div>
          <DialogTitle className="font-heading text-2xl mt-1">
            {sectionLabel} locked
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-1">
            Sirf owner ke liye — 4-digit PIN daaliye.
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            pattern="\d{4}"
            maxLength={4}
            value={pin}
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 4);
              setPin(v);
              setErr("");
              if (v.length === 4) tryUnlock(v);
            }}
            placeholder="• • • •"
            className="w-full text-center bg-slate-800/60 border-2 border-slate-600 focus:border-orange-500 focus:outline-none text-white text-3xl font-mono-num tracking-[0.7em] rounded-sm py-3 disabled:opacity-60"
            data-testid={`pin-lock-input-${sectionKey}`}
            autoComplete="off"
          />
          {err && (
            <div className="text-red-400 text-xs font-mono-num text-center" data-testid={`pin-lock-error-${sectionKey}`}>
              {err}
            </div>
          )}
          <Button
            onClick={() => tryUnlock(pin)}
            disabled={pin.length < 4 || busy}
            className="w-full rounded-sm h-11 bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-40"
            data-testid={`pin-lock-submit-${sectionKey}`}
          >
            <ShieldCheck size={16} weight="fill" className="mr-2" />
            {busy ? "CHECKING…" : "UNLOCK"}
          </Button>
          <div className="text-[10px] font-mono-num text-slate-500 tracking-widest text-center">
            Session ke liye unlock rahega. Logout / browser close pe reset.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Compact indicator to show inside a section header once it's unlocked, so the
   owner knows they're inside a protected area. Optional to use. */
export function UnlockedPill() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">
      <LockOpen size={10} weight="fill" /> OWNER SESSION
    </span>
  );
}
